import type { DsiContainerRef, DsiTarget } from './live-preview';
import { gameKey, gamesOf, type SiteSourceContent } from './site-games';

/**
 * Which game list a game in the live site preview belongs to, from what today's bridge reports. Pure
 * helpers; see `.claude/specs/2026-09-17-preview-game-list-overlays.md` for why this has to be worked out
 * in the host at all.
 */

/** A hovered / clicked game: its id, its position in the list, and the list instance it was rendered by. */
export type GameTile = { gameId: string; index: number; instance: string | null };

/** The list instance a tile node id is scoped to (`dsi-i13#0:craftId` -> `dsi-i13`), or null. */
export const instanceOf = (nodeId: string): string | null => {
  const hash = nodeId.indexOf('#');
  return hash > 0 ? nodeId.slice(0, hash) : null;
};

const looksLikeGame = (data: unknown): boolean => typeof data === 'object' && data !== null && 'provider_id' in data;

/**
 * The game a target is, or null. A tile is an item with an id and a position; it is a GAME when it sits
 * straight on a game list (`slot_game`) or its data carries a `provider_id` (the tile's inner elements are
 * data elements of their own, so their container is not the list).
 */
export const gameTileOf = (target: DsiTarget): GameTile | null => {
  const { item } = target;
  if (!item || item.id === null || item.index === null || !Number.isInteger(item.index) || item.index < 0) return null;
  if (target.container.typeId !== 'slot_game' && !looksLikeGame(item.data)) return null;
  return { gameId: item.id, index: item.index, instance: instanceOf(target.nodeId) };
};

/**
 * The game list a target sits in, as the BRIDGE itself reports it: the innermost `slot_game` container of
 * the chain it now sends with every hover and click. On a tile that is the list which rendered it (the
 * tile's own container is its thumbnail data element); on a gap between tiles it is the list itself. Null
 * on a site deployed before that bridge change — everything below is how the list was worked out then.
 */
export const listFromContainers = (target: DsiTarget): DsiContainerRef | null => {
  const lists = (target.containers ?? []).filter((container) => container.typeId === 'slot_game');
  const innermost = lists[0];
  if (!innermost) return null;
  // The same list shows up TWICE in a real page's chain (verified against the app, 2026-09-18): the
  // source wrapper, and inside it the renderer root that carries `data-ds-inst`. Same rect, but the
  // instance names ONE rendered carousel — so a page that renders a list twice stays unambiguous, and
  // `dsi-highlight` resolves it to the right one.
  return lists.find((container) => container.dsId === innermost.dsId && container.instId) ?? innermost;
};

/** The list a target names itself: its container is one of the page's game lists. */
export const listFromTarget = (target: DsiTarget, gameLists: ReadonlySet<string>): string | null =>
  target.container.typeId === 'slot_game' && gameLists.has(target.container.dsId) ? target.container.dsId : null;

/**
 * The list a tile belongs to, from the lists' contents: the one whose game at the tile's position is that
 * game. The list its instance is already tied to wins; lists tied to OTHER instances are skipped (one list is
 * one carousel on a page, as a rule); then the first remaining candidate. Null when none holds it there.
 */
export const listByContent = (
  tile: GameTile,
  contents: ReadonlyMap<string, SiteSourceContent>,
  instances: ReadonlyMap<string, string>,
): string | null => {
  const known = tile.instance ? instances.get(tile.instance) : undefined;
  if (known) return known;
  const candidates = Array.from(contents.entries())
    .filter(([, content]) => {
      const game = gamesOf(content)[tile.index];
      return game !== undefined && gameKey(game) === tile.gameId;
    })
    .map(([dsId]) => dsId);
  const taken = new Set(Array.from(instances.values()));
  return candidates.find((dsId) => !taken.has(dsId)) ?? candidates[0] ?? null;
};

/** The list itself rather than one of its tiles: the bridge reports the gaps between tiles this way, with
 *  the whole list's rect. */
export const isListGap = (target: DsiTarget): boolean =>
  target.container.typeId === 'slot_game' && target.item === null;
